"use client";

import { CheckIcon, ChevronsUpDownIcon, Loader2Icon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type EntityFilters,
  type EntityOption,
  useEntityOptionLabels,
  useEntityOptions,
} from "@/hooks/use-entity-options";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 50;

interface BaseProps {
  /** Key into the server's OPTION_SOURCES (e.g. "roles", "pillars", "users"). */
  entity: string;
  /** Filter params the source declares (e.g. { status: "active" }, { pillarId }). */
  filters?: EntityFilters;
  /** Ids to omit from search results (selected-value labels still resolve). */
  excludeIds?: string[];
  placeholder?: string;
  /** Text shown in the search box. */
  searchPlaceholder?: string;
  /** Trigger height — `"sm"` (h-8) to match compact filter selects. */
  size?: "sm" | "default";
  disabled?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
}

interface SingleProps extends BaseProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Render a "None" row that clears the value (for nullable FKs). */
  includeNone?: boolean;
  noneLabel?: string;
}

interface MultiProps extends BaseProps {
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * Shared internals for both single- and multi-select. Holds the popover/search
 * state, runs the server-side search, and resolves labels for selected ids that
 * aren't in the current page so the trigger always renders real names.
 */
function useComboboxState(args: {
  entity: string;
  filters?: EntityFilters;
  excludeIds?: string[];
  selectedIds: string[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const debouncedQuery = useDebouncedValue(query, 300);

  const optionsQuery = useEntityOptions(args.entity, {
    search: debouncedQuery,
    filters: args.filters,
    excludeIds: args.excludeIds,
    limit,
    enabled: open,
  });
  const labelsQuery = useEntityOptionLabels(args.entity, args.selectedIds);

  // id → label, merged from the resolved selected labels and the visible page.
  const labelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of labelsQuery.data ?? []) map.set(opt.id, opt.label);
    for (const opt of optionsQuery.data?.data ?? []) map.set(opt.id, opt.label);
    return map;
  }, [labelsQuery.data, optionsQuery.data]);

  function resetSearch() {
    setQuery("");
    setLimit(PAGE_SIZE);
  }

  return {
    open,
    setOpen,
    query,
    setQuery,
    limit,
    setLimit,
    optionsQuery,
    labelMap,
    resetSearch,
  };
}

interface ListBodyProps {
  state: ReturnType<typeof useComboboxState>;
  selectedIds: string[];
  onSelect: (id: string) => void;
  none?: { label: string; onSelect: () => void; selected: boolean };
}

function ComboboxList({ state, selectedIds, onSelect, none }: ListBodyProps) {
  const { optionsQuery, setLimit, limit } = state;
  const options = optionsQuery.data?.data ?? [];
  const hasNext = optionsQuery.data?.hasNext ?? false;
  const selected = new Set(selectedIds);

  return (
    <CommandList>
      {none ? (
        <CommandItem value="__none__" onSelect={none.onSelect}>
          <CheckIcon className={cn("size-4", none.selected ? "opacity-100" : "opacity-0")} />
          <span className="text-muted-foreground">{none.label}</span>
        </CommandItem>
      ) : null}

      {optionsQuery.isError ? (
        <div className="text-destructive px-2 py-6 text-center text-sm">
          Couldn't load options.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => {
              void optionsQuery.refetch();
            }}
          >
            Retry
          </button>
        </div>
      ) : optionsQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
          <Loader2Icon className="size-4 animate-spin" />
          Loading…
        </div>
      ) : options.length === 0 ? (
        <div className="text-muted-foreground py-6 text-center text-sm">No matches.</div>
      ) : (
        options.map((opt: EntityOption) => (
          <CommandItem key={opt.id} value={opt.id} onSelect={() => onSelect(opt.id)}>
            <CheckIcon
              className={cn("size-4", selected.has(opt.id) ? "opacity-100" : "opacity-0")}
            />
            {opt.label}
          </CommandItem>
        ))
      )}

      {hasNext ? (
        <div className="p-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            onClick={() => setLimit(limit + PAGE_SIZE)}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </CommandList>
  );
}

export function triggerClasses(invalid?: boolean, size: "sm" | "default" = "default") {
  return cn(
    "border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm font-normal shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
    size === "sm" ? "h-8" : "h-9",
    invalid && "border-destructive ring-destructive/20",
  );
}

/** Single-select, server-searchable entity picker. */
export function EntityCombobox({
  entity,
  value,
  onChange,
  filters,
  excludeIds,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  includeNone = false,
  noneLabel = "None",
  size = "default",
  disabled,
  className,
  "aria-invalid": ariaInvalid,
}: SingleProps) {
  const selectedIds = useMemo(() => (value ? [value] : []), [value]);
  const state = useComboboxState({ entity, filters, excludeIds, selectedIds });
  const label = value ? (state.labelMap.get(value) ?? "…") : null;

  return (
    <Popover
      open={state.open}
      onOpenChange={(o) => {
        state.setOpen(o);
        if (!o) state.resetSearch();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={state.open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className={cn(triggerClasses(ariaInvalid, size), className)}
        >
          <span className={cn("truncate", !label && "text-muted-foreground")}>
            {label ?? placeholder}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command shouldFilter={false}>
          <CommandInput
            value={state.query}
            onValueChange={state.setQuery}
            placeholder={searchPlaceholder}
          />
          <ComboboxList
            state={state}
            selectedIds={selectedIds}
            onSelect={(id) => {
              onChange(id);
              state.setOpen(false);
              state.resetSearch();
            }}
            none={
              includeNone
                ? {
                    label: noneLabel,
                    selected: !value,
                    onSelect: () => {
                      onChange(null);
                      state.setOpen(false);
                      state.resetSearch();
                    },
                  }
                : undefined
            }
          />
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Multi-select, server-searchable entity picker. */
export function EntityComboboxMulti({
  entity,
  value,
  onChange,
  filters,
  excludeIds,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled,
  className,
  "aria-invalid": ariaInvalid,
}: MultiProps) {
  const state = useComboboxState({ entity, filters, excludeIds, selectedIds: value });

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <Popover
      open={state.open}
      onOpenChange={(o) => {
        state.setOpen(o);
        if (!o) state.resetSearch();
      }}
    >
      <PopoverTrigger asChild>
        {/* A div (not a button) so the per-chip remove buttons aren't nested
            interactive content; keyboard opens via onKeyDown. */}
        <div
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={state.open}
          aria-invalid={ariaInvalid}
          aria-disabled={disabled}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
              e.preventDefault();
              state.setOpen(true);
            }
          }}
          className={cn(
            triggerClasses(ariaInvalid),
            "h-auto min-h-9 cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
            className,
          )}
        >
          <span className="flex flex-1 flex-wrap gap-1">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              value.map((id) => (
                <Badge key={id} variant="secondary" className="gap-1">
                  {state.labelMap.get(id) ?? "…"}
                  <button
                    type="button"
                    aria-label="Remove"
                    className="hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(id);
                    }}
                  >
                    <XIcon className="size-3" />
                  </button>
                </Badge>
              ))
            )}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command shouldFilter={false}>
          <CommandInput
            value={state.query}
            onValueChange={state.setQuery}
            placeholder={searchPlaceholder}
          />
          <ComboboxList state={state} selectedIds={value} onSelect={toggle} />
        </Command>
      </PopoverContent>
    </Popover>
  );
}
